"use client";

import { ArrowRight, Accessibility, Car, MapPin, TrainFront as Train, Phone, Mail, Clock, FileText, Shield } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Location() {
  const header = useRevealOnScroll<HTMLDivElement>();
  const mapWrap = useRevealOnScroll<HTMLDivElement>();
  const footer = useRevealOnScroll<HTMLDivElement>();
  const practical = [{
    icon: Phone,
    label: "PHONE",
    value: "(480) 555-0123",
    link: null
  }, {
    icon: Mail,
    label: "EMAIL",
    value: "admin@tinytoteenservices.org",
    link: null
  }, {
    icon: MapPin,
    label: "SERVICE AREA",
    value: "Arizona Statewide",
    link: null
  }, {
    icon: Clock,
    label: "RESPONSE TIME",
    value: "Within 24-48 Hours",
    link: null
  }, {
    icon: Shield,
    label: "INSURANCE",
    value: "Fully Covered | $1M Liability",
    link: null
  }, {
    icon: FileText,
    label: "POLICIES",
    value: "Payment & Cancellation",
    link: "/policies"
  }];
  return <section data-section-id="3142" id="our-service-area" className="bg-background text-foreground py-24 md:py-32 overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div ref={header.ref} className={`mb-12 md:mb-16 transition-all duration-700 ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="flex items-center gap-3 mb-8">
            <span className="h-px w-8" style={{
            backgroundColor: "var(--primary)"
          }} />
            <span className="font-mono text-xs uppercase tracking-widest" style={{
            color: "var(--primary)"
          }}>
              Service Area
            </span>
          </div>

          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8">
            <h2 className="font-default font-bold text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[0.9] tracking-tighter uppercase">
              Serving
              <br />
              <span className="font-default italic font-light normal-case">
                Arizona
              </span>
              <span style={{
              color: "var(--primary)"
            }}>.</span>
            </h2>
            <p className="font-default italic text-lg md:text-xl text-muted-foreground max-w-sm leading-relaxed">
              Providing neutral, trauma-informed support across the state.
            </p>
          </div>
        </div>
      </div>

      <div ref={mapWrap.ref} className={`relative w-full transition-all duration-1000 ease-out ${mapWrap.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="relative w-full border shadow-lg overflow-hidden" style={{
          height: "600px",
          borderColor: "var(--border)"
        }}>
            <div className="absolute inset-0" style={{
            backgroundColor: "var(--card)",
            backgroundImage: "linear-gradient(var(--border) 1px, transparent 1px), linear-gradient(90deg, var(--border) 1px, transparent 1px)",
            backgroundSize: "80px 80px"
          }} />

            <svg className="absolute inset-0 w-full h-full" viewBox="0 0 1200 600" preserveAspectRatio="none" aria-hidden="true">
              <path d="M0,420 Q200,380 400,400 T800,360 T1200,300" stroke="var(--secondary)" strokeWidth="1.5" fill="none" />
              <path d="M0,180 Q300,220 600,200 T1200,240" stroke="var(--secondary)" strokeWidth="1" fill="none" />
              <path d="M300,0 Q340,150 380,300 T420,600" stroke="var(--secondary)" strokeWidth="1" fill="none" />
              <path d="M900,0 Q880,200 860,400 T840,600" stroke="var(--secondary)" strokeWidth="1" fill="none" />
              <path d="M0,520 Q300,480 550,420 T780,320" stroke="var(--primary)" strokeWidth="2" fill="none" strokeDasharray="4 6" opacity="0.6" />
            </svg>

            <div className="absolute" style={{
            left: "65%",
            top: "48%"
          }} aria-hidden="true">
              <span className="absolute inline-flex h-16 w-16 -translate-x-1/2 -translate-y-1/2 animate-ping opacity-40" style={{
              backgroundColor: "var(--primary)",
              borderRadius: "0px"
            }} />
              <span className="absolute inline-flex h-10 w-10 -translate-x-1/2 -translate-y-1/2 animate-pulse opacity-60" style={{
              backgroundColor: "var(--primary)",
              borderRadius: "0px"
            }} />
              <div className="relative flex items-center justify-center h-12 w-12 -translate-x-1/2 -translate-y-1/2 shadow-md" style={{
              backgroundColor: "var(--primary)",
              borderRadius: "0px"
            }}>
                <MapPin className="h-6 w-6" style={{
                color: "var(--primary-foreground)"
              }} strokeWidth={2.5} />
              </div>
            </div>

            <div className="absolute bottom-6 left-6 md:bottom-10 md:left-10 max-w-[420px] w-[calc(100%-3rem)] md:w-full backdrop-blur-sm border shadow-lg p-8 md:p-12" style={{
            backgroundColor: "var(--background)",
            borderColor: "var(--border)"
          }}>
              <div className="flex items-center gap-3 mb-6">
                <span className="h-px w-6" style={{
                backgroundColor: "var(--primary)"
              }} />
                <span className="font-mono text-[10px] uppercase tracking-widest" style={{
                color: "var(--primary)"
              }}>
                  Agency Location
                </span>
              </div>

              <h3 className="font-default font-bold text-2xl md:text-3xl leading-[1.05] tracking-tight uppercase mb-6">
                Tiny to Teen
                <br />
                Services, LLC
                <span style={{
                color: "var(--primary)"
              }}>.</span>
              </h3>

              <address className="not-italic font-default text-base md:text-lg leading-relaxed text-foreground/90 mb-6">
                Serving Families
                <br />
                Across Arizona
              </address>

              <Separator className="my-6" style={{
              backgroundColor: "var(--border)"
            }} />

              <div className="font-mono text-[11px] uppercase tracking-widest leading-relaxed text-muted-foreground mb-8">
                Professional Support
                <br />
                By Appointment Only
              </div>

              <Button asChild size="lg" className="w-full justify-between font-mono text-xs uppercase tracking-widest h-12 px-6 shadow-md">
                <Link to="/ContactPage">
                  Contact Us
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div ref={footer.ref} className={`mt-12 md:mt-16 transition-all duration-700 ease-out ${footer.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 border-t border-b" style={{
          borderColor: "var(--border)"
        }}>
            {practical.map((item, i) => {
            const Icon = item.icon;
            const content = <div data-index={i} key={item.label} className={`flex items-center gap-4 py-6 md:py-8 px-2 md:px-6 h-full ${i !== practical.length - 1 ? "xl:border-r border-b xl:border-b-0" : ""}`} style={{
              borderColor: "var(--border)"
            }}>
                  <div className="flex items-center justify-center h-10 w-10 shrink-0 border" style={{
                borderColor: "var(--border)",
                color: "var(--primary)"
              }}>
                    <Icon className="h-5 w-5" strokeWidth={1.5} />
                  </div>
                  <div className="flex flex-col">
                    <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                      {item.label}
                    </span>
                    <span className="font-mono text-xs uppercase tracking-tight text-foreground/90">
                      {item.value}
                    </span>
                  </div>
                </div>;
            if (item.link) {
              return <Link key={item.label} to={item.link} className="block hover:bg-accent/50 transition-colors">
                    {content}
                  </Link>;
            }
            return content;
          })}
          </div>
        </div>
      </div>
    </section>;
}